"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";
import { Input, type InputProps } from "./Input";

/** An Input with fixed text beside the value, such as a currency sign. */
function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      className={cn(
        "flex h-9 w-full min-w-0 items-center rounded-sm",
        "border border-border bg-card",
        "transition-[border-color,box-shadow]",
        "has-[input:focus-visible]:border-ring",
        "has-[input:focus-visible]:ring-[3px] has-[input:focus-visible]:ring-ring/50",
        "has-[input[aria-invalid=true]]:border-(--notification-destructive-fg)",
        "has-[input:disabled]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "flex h-full cursor-text items-center body-md text-muted-foreground select-none",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3",
        "inline-end": "order-last pr-3",
      },
    },
    defaultVariants: { align: "inline-start" },
  },
);

interface InputGroupAddonProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof inputGroupAddonVariants> {}

function InputGroupAddon({
  className,
  align,
  onClick,
  ...props
}: InputGroupAddonProps) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a pointer shortcut to the input, which stays the keyboard target
    <div
      data-slot="input-group-addon"
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(event) => {
        onClick?.(event);
        event.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

function InputGroupInput({ className, ...props }: InputProps) {
  return (
    <Input
      data-slot="input-group-input"
      className={cn(
        "h-full flex-1 rounded-none border-0 bg-transparent px-2",
        // Important, so it outranks the focus ring of Input whatever the CSS order.
        "focus-visible:ring-0!",
        className,
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  type InputGroupAddonProps,
  InputGroupInput,
};
